import React, { useState } from 'react';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { blogPosts } from '../mock/data';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

// Web3Forms Access Key
const WEB3FORMS_ACCESS_KEY = '153572c2-262d-4749-8ba6-a2d6aaa74108';

const InsightsPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Market Analysis', 'Investment Strategy', 'Trading Tips'];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `📧 New Newsletter Subscription`,
          from_name: 'Equi Pulse Website',
          form_type: 'Newsletter Subscription',
          email: email
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Successfully subscribed to our newsletter!');
        setEmail('');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Market Insights & Blog | Stock Market Analysis | Equi Pulse"
        description="Read latest market insights, stock analysis, trading strategies and investment tips from Equi Pulse Research experts. Stay updated with Indian stock market trends."
        keywords="market insights, stock market blog, trading strategies, investment tips, market analysis India"
        url="/insights"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Stock Market <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Insights & Analysis</span>
            </h1>
            <p className="text-xl text-gray-300">
              Expert market analysis and investment strategies from SEBI registered research analysts.
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-emerald-900/20 to-slate-800 rounded-2xl border border-emerald-700/30">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-white mb-3">
                Subscribe to Our <span className="text-amber-400">Newsletter</span>
              </h2>
              <p className="text-gray-400">
                Get the latest market insights delivered monthly to your inbox.
              </p>
            </div>

            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
              {/* Honeypot field for spam protection */}
              <input type="checkbox" name="botcheck" className="hidden" style={{ display: 'none' }} />
              
              <label htmlFor="newsletter-email" className="sr-only">Email address for newsletter</label>
              <input
                type="email"
                id="newsletter-email"
                required
                aria-required="true"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 bg-slate-800 border border-emerald-700/30 rounded-lg text-white focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-200"
              />
              <button
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300 whitespace-nowrap"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center" role="group" aria-label="Filter articles by category">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                aria-pressed={selectedCategory === category}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-emerald-700 to-emerald-600 text-white shadow-lg shadow-emerald-900/50'
                    : 'bg-slate-800 text-gray-300 hover:text-amber-400 border border-emerald-700/30'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.id}
                className="group bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={`${post.title} - Stock Market Research by Equi Pulse`}
                    width="400"
                    height="192"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-emerald-700/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      {new Date(post.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" aria-hidden="true" />
                      Equi Pulse
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors duration-200">
                    {post.title}
                  </h3>

                  <p className="text-gray-400 mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>

                  <button className="inline-flex items-center gap-2 text-emerald-400 hover:text-amber-400 font-semibold transition-colors duration-200 group" aria-label={`Read more about ${post.title}`}>
                    Read More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" />
                  </button>
                </div>
              </article>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Want Personalized{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                Market Insights?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Subscribe to our research services for daily market analysis and trade recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300"
              >
                View Services
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-slate-800/50 backdrop-blur-sm border border-amber-500/30 hover:bg-slate-800 text-white font-semibold rounded-lg transition-all duration-300"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
